import { useState } from 'react'
import { FaArrowRightToBracket, FaGavel, FaKey, FaPaperPlane, FaShieldHalved, FaUserPlus } from 'react-icons/fa6'
import logo from '../images/OBC.png'
import background from '../images/background.png'
import apiClient from '../services/apiClient'
import { saveSession } from '../services/authStorage'
import { markPaymentPromptPending } from '../services/paymentOnboardingStorage'
import { navigateTo } from '../app/routes'
import './AuthPage.css'

function AuthPage({ mode = 'login' }) {
  const isRegister = mode === 'register'
  const isForgotPassword = mode === 'forgot-password'
  const isResetPassword = mode === 'reset-password'
  const urlResetToken = new URLSearchParams(window.location.search).get('token') || ''
  const [form, setForm] = useState({ name: '', email: '', password: '' })
  const [status, setStatus] = useState('')
  const [devResetToken, setDevResetToken] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setIsSubmitting(true)
    setStatus('')

    try {
      if (isForgotPassword) {
        const response = await apiClient.requestPasswordReset({ email: form.email })
        setStatus(response.message || 'If the account exists, reset instructions are ready.')
        setDevResetToken(response.resetToken || '')
        return
      }

      const session = isResetPassword
        ? await apiClient.resetPassword({ token: urlResetToken || form.token, password: form.password })
        : isRegister
          ? await apiClient.register(form)
          : await apiClient.login({ email: form.email, password: form.password })

      saveSession(session.token, session.user)
      markPaymentPromptPending(session.user)
      navigateTo(session.user.role === 'admin' ? '/admin' : '/dashboard')
    } catch (error) {
      setStatus(error.message || 'Authentication failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="obc-auth" style={{ backgroundImage: `url(${background})` }}>
      <div className="obc-auth__shade" />
      <section className="obc-auth__panel">
        <div className="obc-auth__brand">
          <img src={logo} alt="OBC Cards and Collectibles" />
          <span><FaShieldHalved /> Veteran owned collector command</span>
        </div>

        <div className="obc-auth__copy">
          <span className="obc-auth__kicker"><FaGavel /> Casino energy. Sports bar pace.</span>
          <h1>
            {isForgotPassword
              ? 'Recover Vault Access'
              : isResetPassword
                ? 'Set New Password'
                : isRegister ? 'Create Collector Access' : 'Sign In To The Vault'}
          </h1>
          <p>
            {isForgotPassword || isResetPassword
              ? 'Reset account access without exposing whether an email is registered. Email delivery can plug into this flow next.'
              : 'Secure access for bidding, weekly bundle checkout, watchlists, won items, and admin operations.'}
          </p>
        </div>

        <form className="obc-auth__form" onSubmit={handleSubmit}>
          {isRegister ? (
            <label>
              Collector name
              <input name="name" value={form.name} onChange={handleChange} minLength="2" required />
            </label>
          ) : null}

          {!isResetPassword ? (
            <label>
              Email
              <input name="email" type="email" value={form.email} onChange={handleChange} required />
            </label>
          ) : null}

          {isResetPassword && !urlResetToken ? (
            <label>
              Reset token
              <input name="token" value={form.token || ''} onChange={handleChange} required />
            </label>
          ) : null}

          {!isForgotPassword ? (
            <label>
              {isResetPassword ? 'New password' : 'Password'}
              <input name="password" type="password" value={form.password} onChange={handleChange} minLength={isRegister || isResetPassword ? 10 : 8} required />
            </label>
          ) : null}

          {status ? <p className="obc-auth__error">{status}</p> : null}
          {devResetToken ? (
            <div className="obc-auth__dev-token">
              <span>Local test reset link</span>
              <a href={`/reset-password?token=${encodeURIComponent(devResetToken)}`}>Open reset form</a>
            </div>
          ) : null}

          <button type="submit" disabled={isSubmitting}>
            {isForgotPassword ? <FaPaperPlane /> : isResetPassword ? <FaKey /> : isRegister ? <FaUserPlus /> : <FaArrowRightToBracket />}
            {isSubmitting
              ? 'Processing...'
              : isForgotPassword
                ? 'Send Reset Instructions'
                : isResetPassword
                  ? 'Reset Password'
                  : isRegister ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <div className="obc-auth__switch">
          {isForgotPassword || isResetPassword ? (
            <button type="button" onClick={() => { navigateTo('/login') }}>Back to sign in</button>
          ) : isRegister ? (
            <button type="button" onClick={() => { navigateTo('/login') }}>Already have access? Sign in</button>
          ) : (
            <>
              <button type="button" onClick={() => { navigateTo('/register') }}>Need an account? Register</button>
              <button type="button" onClick={() => { navigateTo('/forgot-password') }}>Forgot password?</button>
            </>
          )}
        </div>
      </section>
    </main>
  )
}

export default AuthPage
